/**
 * The phone's root layout, for the browser: who the person is, which business
 * is open, and the background work that keeps this browser's copy current.
 * Everything that reads a tenant sits inside ActiveTenantProvider.
 *
 * The router draws this around its pages (`InnerWrap` in `router.tsx`), not
 * the root route, because the router draws no route at all until every chunk
 * of the page it is opening has arrived. Here, the session and `/v1/me`
 * requests and the first sync start with the app, while that code loads,
 * instead of after it.
 */
import { countryFor } from '@alluzo/shared';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useState, useSyncExternalStore, type ReactElement, type ReactNode } from 'react';

import { BootSplash } from '~/app/boot-splash';
import { RouteError } from '~/app/route-error';
import { SearchIndexManager, SyncManager } from '~/app/managers';
import { TooltipProvider } from '~/components/ui/tooltip';
import { forgetThumbs } from '~/features/report-templates/thumbs';

import { PhoneCountryProvider } from '@ui/fields/phone-country';

import { ActiveTenantProvider, useBusinessCountry } from '@/hooks/use-active-tenant';
import { MeProvider, useMe } from '@/hooks/use-me';
import { useSessionStatus } from '@/hooks/use-session-status';
import { databaseIsOpen, forgetMirror, heldMirrorAtLoad, onDatabaseOpen, openDatabase } from '@/lib/db';

/**
 * The boot screen until the router draws its first page. index.html shows the
 * same screen before any script runs, and React clears it on its first draw,
 * which comes before the page's code has arrived: without this the window
 * would go blank between the two.
 */
function UntilFirstPage(): ReactElement | null {
  const waiting = useRouterState({ select: (state) => state.matches.length === 0 });
  return waiting ? <BootSplash /> : null;
}

/**
 * The database for a session that needs it (`src/platform/db.ts`).
 *
 * A signed-out page draws at once, over the empty database: SQLite is not
 * even downloaded. A signed-in session, or a browser that already holds a
 * mirror, waits on the boot screen until the real database is open, with
 * every provider below unmounted, so nothing can read or write the mirror
 * before it is there. The sign-out that wipes the mirror (`MeProvider`'s
 * `forgetDevice`, which runs before this effect, being a child's) saves the
 * wiped copy and then drops the hint, so the next visit's sign-in page skips
 * SQLite again. A database that cannot open says so, with Try again.
 */
function DatabaseGate({ children }: { children: ReactNode }): ReactElement {
  const session = useSessionStatus();
  const open = useSyncExternalStore(onDatabaseOpen, databaseIsOpen, databaseIsOpen);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const needed = heldMirrorAtLoad || session === 'signed-in';

  useEffect(() => {
    if (needed && !open) openDatabase().catch((error: unknown) => setFailure({ error }));
  }, [needed, open, attempt]);

  useEffect(() => {
    if (open && session === 'signed-out') void forgetMirror();
  }, [open, session]);

  // Template thumbnails print under the viewer's mask: none outlives the session that drew it.
  useEffect(() => {
    if (session === 'signed-out') forgetThumbs();
  }, [session]);

  if (failure !== null) {
    const retry = (): void => {
      setFailure(null);
      setAttempt((count) => count + 1);
    };
    return <RouteError error={failure.error} reset={retry} />;
  }
  if (needed && !open) return <BootSplash />;
  return <>{children}</>;
}

/**
 * The country every phone field opens on when it names none (D6): the open
 * workspace's, off its locale, then the person's own region. The field asks
 * the browser after these (`features/fields/text-phone.tsx`).
 */
function PhoneCountryRoot({ children }: { children: ReactNode }): ReactElement {
  const workspace = useBusinessCountry();
  const { state } = useMe();
  const region = state.status === 'ready' ? state.me.user.region : null;
  const iso = workspace ?? (region === null ? null : (countryFor(region)?.iso ?? null));
  return <PhoneCountryProvider iso={iso}>{children}</PhoneCountryProvider>;
}

export function RootProviders({ children }: { children: ReactNode }): ReactElement {
  return (
    <DatabaseGate>
      <MeProvider>
        <ActiveTenantProvider>
          <PhoneCountryRoot>
            <TooltipProvider delayDuration={300}>
              <SyncManager />
              <SearchIndexManager />
              <UntilFirstPage />
              {children}
            </TooltipProvider>
          </PhoneCountryRoot>
        </ActiveTenantProvider>
      </MeProvider>
    </DatabaseGate>
  );
}
